import { Group, Mesh, Object3D, Scene, Vector3 } from 'three';
import type { CharacterAnimator } from '../character/CharacterAnimator';
import type { CharacterInstance, CharacterLoader } from '../character/CharacterModel';
import { WORLD } from '../config';
import type { HeightField } from '../world/HeightField';
import { JumpingFish } from './JumpingFish';
import { RARITY_COLORS, RARITY_LABELS, rollFish, type FishType } from './fishTypes';

export type FishingPhase = 'off' | 'casting' | 'waiting' | 'bite' | 'reeling' | 'caught';

export interface FishingEvents {
  /** Krátká hláška do rohu obrazovky. */
  onPrompt?: (text: string, color?: string) => void;
  /** Úlovek — vhodné poslat do chatu. */
  onCatch?: (fish: FishType) => void;
  /** Místo nahození se má oznámit ostatním; null při ukončení. */
  onSpotChange?: (p: [number, number] | null) => void;
}

/** Pod jakým klíčem má splávek vlastní hráč. */
const LOCAL_ID = 'self';

/** Jak daleko před postavu se nahazuje. */
const CAST_DISTANCE = 7;
/** Minimální hloubka vody v místě dopadu. */
const MIN_DEPTH = 1.0;
/** Rozmezí čekání na zábr. */
const WAIT_MIN = 3.5;
const WAIT_MAX = 9;
/** Kolik času má hráč na zaseknutí. */
const BITE_WINDOW = 1.6;
/** Jak dlouho se navíjí. */
const REEL_TIME = 2.2;
/** Jak dlouho se ukazuje úlovek. */
const SHOW_TIME = 3.0;

/**
 * Rybaření.
 *
 * Stavový automat: nahození → čekání → zábr → navíjení → úlovek. Každá fáze
 * má vlastní animaci a vlastní časovač; přechody řídí jen `update()`, takže
 * není možné se dostat do dvou stavů naráz ani zapomenout na úklid.
 *
 * Vědomé rozhodnutí: **rybaření je čistě klientská záležitost.** Server o něm
 * neví a úlovek nevaliduje. Pro kooperativní svět bez ekonomiky je to
 * v pořádku — kdyby ryby jednou měly cenu, musí losování i kontrola vody
 * přejít na server, protože takhle si je hráč může naklikat v konzoli.
 * Ostatní hráči vidí jen animaci, která se posílá jako každá jiná.
 */
export class FishingSystem {
  private readonly bobber = new Group();
  private readonly jumpers: JumpingFish;
  private readonly castPoint = new Vector3();
  private readonly forward = new Vector3();

  private phase: FishingPhase = 'off';
  private timer = 0;
  private hooked: FishType | null = null;
  private trophy: Object3D | null = null;
  private bobberReady = false;

  constructor(
    scene: Scene,
    private readonly loader: CharacterLoader,
    private readonly heightField: HeightField,
    private readonly events: FishingEvents = {},
    jumpers?: JumpingFish,
  ) {
    this.bobber.name = 'bobber';
    this.bobber.visible = false;
    scene.add(this.bobber);
    // Hejno je sdílené se vzdálenými hráči — jeden fond modelů pro celý svět.
    this.jumpers = jumpers ?? new JumpingFish(scene, loader);
  }

  /** Hejno pro splávky ostatních hráčů. */
  get fishSchool(): JumpingFish {
    return this.jumpers;
  }

  get isActive(): boolean {
    return this.phase !== 'off';
  }

  get currentPhase(): FishingPhase {
    return this.phase;
  }

  /** Načte splávek a fond ryb dopředu, ať první nahození nezadrhne. */
  async preload(): Promise<void> {
    await this.jumpers.preload();
    if (this.bobberReady) return;

    try {
      const gltf = await this.loader.loadGLTF('/models/fishing/fishing_gear.glb');
      const source = gltf.scene.getObjectByName('Lure_1');
      if (source) {
        const lure = source.clone(true);
        lure.scale.setScalar(0.35);
        lure.traverse((child) => {
          if (child instanceof Mesh) child.frustumCulled = false;
        });
        this.bobber.add(lure);
        this.bobberReady = true;
      }
    } catch (error) {
      console.warn('[fishing] splávek se nepodařilo načíst:', error);
    }
  }

  /**
   * Zjistí, jestli se z aktuální pozice dá nahodit, a kam.
   *
   * Nahazuje se dopředu ve směru pohledu postavy. Voda musí být dost hluboká,
   * jinak by splávek ležel na mokrém písku a ryby vyskakovaly z mělčiny.
   */
  findCastPoint(character: CharacterInstance, out: Vector3): boolean {
    const origin = character.root.position;
    const yaw = character.root.rotation.y;
    this.forward.set(Math.sin(yaw), 0, Math.cos(yaw));

    // Hledá se první dost hluboké místo, ne pevná vzdálenost — hráč stojící
    // dva metry od břehu by jinak nahodil na souš za vodou.
    for (let d = 2; d <= CAST_DISTANCE; d += 0.75) {
      const x = origin.x + this.forward.x * d;
      const z = origin.z + this.forward.z * d;
      const depth = WORLD.seaLevel - this.heightField.heightAt(x, z);
      if (depth >= MIN_DEPTH) {
        out.set(x, WORLD.seaLevel, z);
        return true;
      }
    }
    return false;
  }

  /** Zahájí rybaření. Vrací false, když v dosahu není voda. */
  start(character: CharacterInstance, animator: CharacterAnimator): boolean {
    if (this.phase !== 'off') return false;
    if (!this.findCastPoint(character, this.castPoint)) {
      this.events.onPrompt?.('Tady se nedá rybařit — postav se k vodě.', '#ffd479');
      return false;
    }

    this.phase = 'casting';
    const clip = character.animations.get('Fishing_Cast');
    this.timer = clip ? clip.duration : 1.9;
    animator.playFishing('fish_cast');
    return true;
  }

  /** Ukončí rybaření a uklidí po sobě. */
  stop(animator?: CharacterAnimator): void {
    this.phase = 'off';
    this.timer = 0;
    this.hooked = null;
    this.bobber.visible = false;
    this.jumpers.removeSpot(LOCAL_ID);
    this.events.onSpotChange?.(null);
    this.clearTrophy();
    animator?.setMovementState(false, false, false);
  }

  /**
   * Zaseknutí. Ve fázi zábru chytá rybu, jinak nedělá nic — kliknutí
   * mimo okno tedy neuškodí, jen se ignoruje.
   */
  hook(character: CharacterInstance, animator: CharacterAnimator): boolean {
    if (this.phase !== 'bite') return false;
    this.phase = 'reeling';
    this.timer = REEL_TIME;
    this.jumpers.removeSpot(LOCAL_ID);
    this.events.onSpotChange?.(null);
    animator.playFishing('fish_reel');
    void character;
    return true;
  }

  /**
   * @param worldTime společný čas světa — z něj se počítají skoky ryb,
   *        aby je všichni hráči viděli stejně
   */
  update(dt: number, worldTime: number, character: CharacterInstance, animator: CharacterAnimator): void {
    this.jumpers.update(worldTime);
    if (this.phase === 'off') return;

    this.timer -= dt;

    switch (this.phase) {
      case 'casting':
        if (this.timer <= 0) {
          this.bobber.position.copy(this.castPoint);
          this.bobber.visible = this.bobberReady;
          this.setLocalSpot();
          this.phase = 'waiting';
          this.timer = WAIT_MIN + Math.random() * (WAIT_MAX - WAIT_MIN);
          this.events.onPrompt?.('Čekáš na zábr…');
          animator.playFishing('fish_idle');
        }
        break;

      case 'waiting':
        // Splávek se pohupuje, aby hladina nevypadala mrtvě.
        this.bobber.position.y =
          WORLD.seaLevel + Math.sin(performance.now() * 0.004) * 0.06;
        if (this.timer <= 0) {
          this.phase = 'bite';
          this.timer = BITE_WINDOW;
          this.hooked = rollFish();
          animator.playFishing('fish_bite');
          this.events.onPrompt?.('ZABRALO! Stiskni F', '#d9b872');
        }
        break;

      case 'bite':
        // Prudké cukání splávku.
        this.bobber.position.y = WORLD.seaLevel - 0.12 + Math.sin(performance.now() * 0.03) * 0.14;
        if (this.timer <= 0) {
          this.hooked = null;
          this.phase = 'waiting';
          this.timer = WAIT_MIN + Math.random() * (WAIT_MAX - WAIT_MIN);
          this.setLocalSpot();
          this.events.onPrompt?.('Utekla ti. Čekej dál…', '#ff8080');
          animator.playFishing('fish_idle');
        }
        break;

      case 'reeling':
        if (this.timer <= 0) {
          this.phase = 'caught';
          this.timer = SHOW_TIME;
          this.bobber.visible = false;
          animator.playFishing('fish_catch');
          if (this.hooked) {
            void this.showTrophy(character, this.hooked);
            this.events.onCatch?.(this.hooked);
            this.events.onPrompt?.(
              `Chytil jsi: ${this.hooked.name} (${RARITY_LABELS[this.hooked.rarity]})`,
              RARITY_COLORS[this.hooked.rarity],
            );
          }
        }
        break;

      case 'caught':
        if (this.timer <= 0) {
          this.clearTrophy();
          // Po úlovku se rovnou nahazuje znovu — hráč nemusí mačkat F dokola.
          this.phase = 'casting';
          const clip = character.animations.get('Fishing_Cast');
          this.timer = clip ? clip.duration : 1.9;
          animator.playFishing('fish_cast');
        }
        break;
    }
  }

  dispose(): void {
    this.clearTrophy();
    this.jumpers.dispose();
    this.bobber.removeFromParent();
  }

  // --- vnitřní ----------------------------------------------------------

  /** Ukáže ulovenou rybu v ruce postavy. */
  /** Zaregistruje vlastní splávek a oznámí ho ostatním. */
  private setLocalSpot(): void {
    this.jumpers.setSpot(LOCAL_ID, this.castPoint);
    this.events.onSpotChange?.([this.castPoint.x, this.castPoint.z]);
  }

  private async showTrophy(character: CharacterInstance, fish: FishType): Promise<void> {
    this.clearTrophy();
    try {
      const gltf = await this.loader.loadGLTF('/models/fishing/fish.glb');
      const source = gltf.scene.getObjectByName(fish.model);
      if (!source) return;

      // Levá ruka drží prut, úlovek jde do pravé... prut visí na pravé,
      // takže trofej dáme do levé, aby se nepřekrývaly.
      const hand = character.root.getObjectByName('hand_l');
      if (!hand) return;

      const trophy = source.clone(true);
      trophy.scale.setScalar(fish.scale * 0.9);
      trophy.position.set(0, 0.08, 0);
      trophy.traverse((child) => {
        if (child instanceof Mesh) {
          child.castShadow = true;
          child.frustumCulled = false;
        }
      });
      hand.add(trophy);
      this.trophy = trophy;
    } catch (error) {
      console.warn('[fishing] trofej se nepodařilo zobrazit:', error);
    }
  }

  private clearTrophy(): void {
    this.trophy?.removeFromParent();
    this.trophy = null;
  }
}
